import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { FriendLocation } from '../types';
import { friendsAPI } from '../services/api';

interface FriendsDataContextType {
  locations: FriendLocation[];
  loading: boolean;
  error: string;
  loadFriendsLocations: () => Promise<void>;
  refreshInBackground: () => Promise<void>;
}

const FriendsDataContext = createContext<FriendsDataContextType | undefined>(undefined);

export const FriendsDataProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [locations, setLocations] = useState<FriendLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadFriendsLocations = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const { locations: friendLocations } = await friendsAPI.getFriendsLocations();
      setLocations(friendLocations);
    } catch (err: any) {
      console.error('Failed to load friends locations:', err);
      setError('Failed to load friends locations');
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh in background without showing loading state
  const refreshInBackground = useCallback(async () => {
    try {
      const { locations: friendLocations } = await friendsAPI.getFriendsLocations();
      setLocations(friendLocations);
      setError('');
    } catch (err: any) {
      console.error('Background refresh failed:', err);
      // Don't update error state - keep showing cached data
    }
  }, []);

  return (
    <FriendsDataContext.Provider
      value={{
        locations,
        loading,
        error,
        loadFriendsLocations,
        refreshInBackground,
      }}
    >
      {children}
    </FriendsDataContext.Provider>
  );
};

export const useFriendsData = () => {
  const context = useContext(FriendsDataContext);
  if (context === undefined) {
    throw new Error('useFriendsData must be used within a FriendsDataProvider');
  }
  return context;
};
